import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { isEqual } from 'lodash';
import {
  ModalService,
  TwoButtonModalV2Component,
  InputV2Module,
  ButtonV2Module,
} from '@schenck/rotec-components';
import { BehaviorSubject } from 'rxjs';
import {
  ReportTemplateField,
  ReportTemplateFieldCategory,
  ReportTemplateRegion,
} from 'src/dto/report-template';
import { TemplateCanvasActionType } from '../template-canvas-action-type.enum';
import { NgClass, AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { TemplateCanvasItemComponent } from '../template-canvas-item/template-canvas-item.component';

@Component({
  selector: 'rc-template-canvas-region',
  templateUrl: './template-canvas-region.component.html',
  styleUrls: ['./template-canvas-region.component.less'],
  imports: [
    TranslatePipe,
    TranslateDirective,
    InputV2Module,
    NgClass,
    TemplateCanvasItemComponent,
    ButtonV2Module,
    AsyncPipe,
  ],
})
export class TemplateCanvasRegionComponent implements OnInit {
  private readonly modalService = inject(ModalService);

  @Input() name: string;
  @Input() columns: number;
  @Input() region: ReportTemplateRegion;
  @Input() allowedFieldCategories: ReportTemplateFieldCategory[];
  @Output() regionChange: EventEmitter<ReportTemplateRegion>;

  items$: BehaviorSubject<ReportTemplateField[][]> = new BehaviorSubject([]);

  constructor() {
    this.regionChange = new EventEmitter<ReportTemplateRegion>();
  }

  ngOnInit() {
    this.renderGrid();
  }

  onItemChanged(itemChangeInfo: [TemplateCanvasActionType, ReportTemplateField]): void {
    const actionType = itemChangeInfo[0];
    const item = itemChangeInfo[1];
    const index = this.region.fields.findIndex((f) => isEqual(f.position, item.position));

    switch (actionType) {
      case TemplateCanvasActionType.Add:
        if (index < 0) {
          this.region.fields.push(item);
        } else {
          this.region.fields[index] = item;
        }
        break;

      case TemplateCanvasActionType.Remove:
        if (index >= 0) {
          this.region.fields.splice(index, 1);
        }
        break;
    }

    this.regionChange.emit(this.region);
  }

  addRow() {
    this.region.rows = this.region.rows < 20 ? this.region.rows + 1 : this.region.rows;
    this.renderGrid();
    this.regionChange.emit(this.region);
  }

  removeRow() {
    const field = this.region.fields.find((field) => field.position.item1 === this.region.rows - 1);

    if (field) {
      const modal = this.modalService.open(TwoButtonModalV2Component, {
        title: 'COMMON_Delete',
        text: 'Do you really want to delete this row and its content?',
        summary: 'Delete Row',
        confirmText: 'COMMON_Delete',
        cancelText: 'Cancel',
        warning: true,
        darkMode: true,
      });

      modal.events$.subscribe((confirm) => {
        if (confirm) {
          this.region.fields = this.region.fields.filter(
            (field) => field.position.item1 !== this.region.rows - 1
          );
          this.deleteRow();
        }
      });
    } else {
      this.deleteRow();
    }
  }

  private deleteRow() {
    this.region.rows = this.region.rows > 1 ? this.region.rows - 1 : this.region.rows;
    this.renderGrid();
    this.regionChange.emit(this.region);
  }

  private renderGrid() {
    const items = this.createGrid(this.region.rows, this.columns);

    if (this.region?.fields) {
      for (let field of this.region.fields) {
        const rowIdx = field.position.item1;
        const colIdx = field.position.item2;

        items[rowIdx][colIdx] = field;
      }
    }
    this.items$.next(items);
  }

  private createGrid(numberOfRows: number, numberOfColumns: number): ReportTemplateField[][] {
    let grid = new Array(numberOfRows);

    for (let i = 0; i < grid.length; i++) {
      grid[i] = new Array(numberOfColumns);
    }

    return grid;
  }
}
